import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { useOptionalBookmarkSelection } from './BookmarkSelectionContext';
import { useBatchActions } from '../hooks/useBatchActions';
import BookmarkDragOverlay from '../components/Bookmark/BookmarkDragOverlay';

export interface BookmarkDragData {
  type: 'bookmark';
  bookmarkIds: string[];
  singleBookmarkId: string;
  bookmarkTitle?: string;
}

export interface DropTargetData {
  type: 'folder' | 'tag' | 'home' | 'archive' | 'liked';
  folderId?: string;
  tagName?: string;
}

interface BookmarkDndContextValue {
  isDragging: boolean;
  draggedBookmarkIds: string[];
  activeDropTargetId: string | null;
}

const BookmarkDndContext = createContext<BookmarkDndContextValue | undefined>(undefined);

export function useBookmarkDnd() {
  const context = useContext(BookmarkDndContext);
  if (!context) {
    throw new Error('useBookmarkDnd must be used within BookmarkDndProvider');
  }
  return context;
}

export function useOptionalBookmarkDnd() {
  return useContext(BookmarkDndContext);
}

interface BookmarkDndProviderProps {
  children: ReactNode;
}

export function BookmarkDndProvider({ children }: BookmarkDndProviderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [draggedBookmarkIds, setDraggedBookmarkIds] = useState<string[]>([]);
  const [draggedBookmarkTitle, setDraggedBookmarkTitle] = useState<string | undefined>();
  const [activeDropTargetId, setActiveDropTargetId] = useState<string | null>(null);

  const selectionContext = useOptionalBookmarkSelection();
  const disableBatchMode = selectionContext?.disableBatchMode;
  const { batchMove, batchMoveHome, batchTag, batchArchive, batchLike } =
    useBatchActions();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // activation threshold
      },
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const data = active.data.current as BookmarkDragData | undefined;

    if (data?.type === 'bookmark') {
      setIsDragging(true);
      setDraggedBookmarkIds(data.bookmarkIds);
      setDraggedBookmarkTitle(data.bookmarkTitle);
    }
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const targetId = (event.over?.id as string) ?? null;
    setActiveDropTargetId(targetId);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      setIsDragging(false);
      setDraggedBookmarkIds([]);
      setDraggedBookmarkTitle(undefined);
      setActiveDropTargetId(null);

      if (!over) return;

      const dragData = active.data.current as BookmarkDragData | undefined;
      const dropData = over.data.current as DropTargetData | undefined;

      if (!dragData || dragData.type !== 'bookmark' || !dropData) return;

      const bookmarkIds = dragData.bookmarkIds;

      switch (dropData.type) {
        case 'folder':
          if (dropData.folderId) {
            batchMove.mutate({ bookmarkIds, folderId: dropData.folderId });
            disableBatchMode?.();
          }
          break;

        case 'home':
          batchMoveHome.mutate(bookmarkIds);
          disableBatchMode?.();
          break;

        case 'archive':
          batchArchive.mutate(bookmarkIds);
          disableBatchMode?.();
          break;

        case 'liked':
          batchLike.mutate(bookmarkIds);
          disableBatchMode?.();
          break;

        case 'tag':
          if (dropData.tagName) {
            batchTag.mutate({
              bookmarkIds,
              addTags: [dropData.tagName],
            });
            disableBatchMode?.();
          }
          break;
      }
    },
    [batchMove, batchMoveHome, batchTag, batchArchive, batchLike, disableBatchMode],
  );

  const handleDragCancel = useCallback(() => {
    setIsDragging(false);
    setDraggedBookmarkIds([]);
    setDraggedBookmarkTitle(undefined);
    setActiveDropTargetId(null);
  }, []);

  return (
    <BookmarkDndContext.Provider
      value={{
        isDragging,
        draggedBookmarkIds,
        activeDropTargetId,
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
        <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
          {isDragging && (
            <BookmarkDragOverlay
              bookmarkIds={draggedBookmarkIds}
              bookmarkTitle={draggedBookmarkTitle}
            />
          )}
        </DragOverlay>
      </DndContext>
    </BookmarkDndContext.Provider>
  );
}
